export interface FirmViewingTagSelection {
  source: 'firm';
  tagId: number;
}

export interface UserViewingTagSelection {
  source: 'user';
  tagId: number;
}

export type ViewingTagSelection =
  | FirmViewingTagSelection
  | UserViewingTagSelection
  | null;

function parseFiniteId(value: string): number | null {
  const normalized = value.trim();
  if (normalized === '') {
    return null;
  }
  const id = Number(normalized);
  return Number.isFinite(id) ? id : null;
}

export function parseViewingTagSelection(
  value: string | null | undefined,
): ViewingTagSelection {
  if (value?.startsWith('firm:')) {
    const tagId = parseFiniteId(value.slice('firm:'.length));
    return tagId === null ? null : { source: 'firm', tagId };
  }
  if (value?.startsWith('user:')) {
    const tagId = parseFiniteId(value.slice('user:'.length));
    return tagId === null ? null : { source: 'user', tagId };
  }
  if (value !== null && value !== undefined && value.trim() !== '') {
    const tagId = parseFiniteId(value);
    return tagId === null ? null : { source: 'firm', tagId };
  }
  return null;
}

export function serializeViewingTagSelection(
  selection: ViewingTagSelection,
): string {
  if (selection?.source === 'firm') {
    return `firm:${String(selection.tagId)}`;
  }
  if (selection?.source === 'user') {
    return `user:${String(selection.tagId)}`;
  }
  return '';
}

export function getFirmViewingTagId(
  selection: ViewingTagSelection,
): number | null {
  return selection?.source === 'firm' ? selection.tagId : null;
}

interface ViewingTagAwareArgs {
  InvestorTagId?: number | null;
  TagId?: number | null;
}

/**
 * True when the caller explicitly set `InvestorTagId` (including the undefined
 * sentinel used by `skipViewingInvestorTagForAccountsQuery` in `accountsApi`).
 */
export function viewingInvestorTagMergeUsesCallersInvestorTagId(arg: {
  InvestorTagId?: number | null | undefined;
}): boolean {
  if (
    Object.prototype.hasOwnProperty.call(arg, 'InvestorTagId') &&
    arg.InvestorTagId === undefined
  ) {
    return true;
  }
  if (
    typeof arg.InvestorTagId === 'number' &&
    Number.isFinite(arg.InvestorTagId)
  ) {
    return true;
  }
  return false;
}

export function mergeViewingTagIntoArg<T extends ViewingTagAwareArgs>(
  arg: T,
  selection: ViewingTagSelection,
): T {
  if (
    viewingInvestorTagMergeUsesCallersInvestorTagId(arg) ||
    (typeof arg.TagId === 'number' && Number.isFinite(arg.TagId))
  ) {
    return arg;
  }
  if (selection === null) {
    return arg;
  }
  // System A: both firm and personal (user) tags filter via `InvestorTagId`.
  return { ...arg, InvestorTagId: selection.tagId };
}
